import invariant from "invariant";
import SearchFormStore from "data/SearchFormStore";
import SearchFormActions from "data/SearchFormActions";
import {
    getActiveRecord,
    getChildren,
} from "data/selectors/records";
import LoadObject from "data/LoadObject";
import TableStore from "data/TableStore";
import Dispatcher from "Dispatcher";

export default class BasePlugin extends Backbone.Model {
    defaults() {
        return {
            id: "baseplugin",
            name: "Unknown Plugin",
            fluxContext: "CTX_MappingPlugin",
            fields: []
        };
    }

    initialize(options = {}) {
        this.options = options;

        this.fluxContext = options.fluxContext;

        this.defaultValuesFromForm = {};

        this.activeRecord = this.options.activeRecord;
        this.entity = this.options.entity;

        this.mapping = this.options.mapping || {};

        this.org = this.options.org;

        this.filterRecords = this.filterRecords.bind(this);
        this.on("change:fields", (_, v) => v.forEach(f => {
            if (!f.id) f.id = "mpfi-" + this.get("id") + "-" + f.key;
        }));
    }

    loadFields() {
        // overload me
        Dispatcher.contextDispatch(this.get("fluxContext"), {
            type: SearchFormActions.SELECT_STEREOTYPE,
            fields: this.get("fields"),
            key: this.get("id"),
            useDateRanges: false, // no date ranges for plugin search queries.
            useMissingCheckbox: false, // no 'missing' checkboxes for plugin search queries.
        });
    }

    getDefaultValues(record) {
        if (this.hasSearchScript()) {
            if (record == null) {
                record = getActiveRecord(TableStore, {
                    key: this.activeRecord.entityKey,
                    id: this.activeRecord.id,
                });
            }
            const qs = this.mapping.searchScript(record);
            return this.parseSearchString(qs);
        }
        return {};
    }

    hasPaginationParams() {
        return false;
    }

    hasSearchScript() {
        return this.mapping.searchScript && typeof this.mapping.searchScript === "function";
    }

    deriveDefaultValuesFromActiveRecord() {
        if (this.hasSearchScript()) {
            this.defaultValuesFromForm = this.getDefaultValues();

            const fields = SearchFormStore.getFields(this.get("fluxContext"));
            fields.forEach((field) => {
                if(this.defaultValuesFromForm[field.key]) {
                    // set the default field value
                    Dispatcher.contextDispatch(this.get("fluxContext"), {
                        type: SearchFormActions.SET_FIELD,
                        path: field.path,
                        value: this.getFormattedValue(field, this.defaultValuesFromForm[field.key], true),
                    });
                }
            });
        }
    }

    parseSearchString(searchString) {
        if (searchString == null) return {};
        const arr = searchString.split("&");
        return _.chain(arr)
            // This handles the case of searchString being an empty string.
            .compact()
            .map((it) => it.split("="))

            // Armusscript serializes null values as 'null'.
            // If a user needs to enter the string literal 'null'
            // into a form we will need to revisit this.
            //
            // Also filtering out any 'falsy' values
            .filter((it) => it[1] !== "null" && it[1])
            .object()
            .value();
    }

    collectionSearchParams() {
        const fields = SearchFormStore.getFields(this.get("fluxContext"));
        const params = _.chain(fields)
            .map((field) => [
                field.key,
                this.getFormattedValue(field, field.value)
            ])
            // Filter out 'falsy' values
            .filter((it) => it[1])
            .object()
            .value();

        if(this.hasPaginationParams()) {
            params.pagesize = SearchFormStore.getPageSize(this.get("fluxContext"));
            params.pagenumber = SearchFormStore.getPage(this.get("fluxContext"));
        }
        return params;
    }

    filterRecords(records) {
        const results = [];

        if (this.mapping.types == null) return results;
        const types = new Set(this.mapping.types);
        if (types.size === 0) return results;

        const visited = new Set();
        const addChildRecords = records =>
            records.forEach(addRecord);
        const addRecord = (record) => {
            if (visited.has(record.getKey())) return;
            visited.add(record.getKey());
            if (types.has(record.entityKey)) results.push(record);
            addChildRecords(getChildren(record, true));
        };
        addChildRecords(records);

        return results;
    }

    getButtonListingPrefix() {
        return "Map From ";
    }

    // This may need to be someplace else.
    // We cannot assume any given endpoints
    // will accept search params in the same
    // format.
    getFormattedValue(field, value, direction = false) {
        if (!value) {
            return;
        }

        switch(field.type) {
            case "date":
                return direction ? moment(value) : moment(value).format("YYYY-MM-DD");

            default:
                return value;
        }
    }

    getRegisterNotifySearchCallback(callback) {
        if (this.mapping.registerNotifySearchCallback) {
            this.mapping.registerNotifySearchCallback(this, this.activeRecord, () => {
                this.deriveDefaultValuesFromActiveRecord();
                callback();
            });
        }
    }

    getQueryLO(query, initiate, interactive) {
        invariant(false,
            "Subclasses of BasePlugin must override getQueryLO() with their own implementation.");
    }

    searchFromActiveRecord() {
        if (!this.hasSearchScript()) return LoadObject.empty();
        const params = this.getDefaultValues();
        if (Object.keys(params).filter(it => params[it]).length === 0) {
            // no params means no search
            return LoadObject.empty();
        }
        return this._searchInternal(params, true, false);
    }

    searchInteractive(initiate = false) {
        const params = this.collectionSearchParams();
        return this._searchInternal(params, initiate, true);
    }

    _searchInternal(params, initiate, interactive) {
        return this.getQueryLO(jQuery.param(params), initiate, interactive)
            .map(this.filterRecords);
    }

    searchPaginationResult() {
        if(!this.hasPaginationParams()) {
            return {
                page: 1,
                pageCount: 0,
                pageSize: 25,
                totalResultCount: 0,
            };
        }
        const params = this.collectionSearchParams();
        return this.getPagination(jQuery.param(params));
    }

    // this is so ADT can have a hard-coded format, ignoring the metamodel
    getLabelForResult(entityProxy) {
        return entityProxy.getLabel();
    }

    validate() {
        const fluxContext = this.get("fluxContext");
        if(SearchFormStore.getFields(fluxContext).length > 0) {
            return SearchFormStore.hasValues(fluxContext);
        }
        return true;
    }

    loadingMessage() {
        return {
            label: "Loading..."
        };
    }
}



// WEBPACK FOOTER //
// ./app/lib/mapping/sources/base/BasePlugin.js